import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ContextualMenu,
  type ContextualMenuItem,
} from '@components/ui/ContextualMenu'
import { InsurancePlan } from '@dataTypes/insurance.types'
import { Discount } from '@dataTypes/discount.type'
import { ITreatmentPlan } from '@dataTypes/treatmentPlan.types'
import { TX_PLAN_DISCOUNT_CUSTOM_ID } from '@utils/treatment-plan.utils'
import type { TxPlanProviderMenuAvailability } from '../TreatmentPlanMain.types'
import './PlanActionMenuButton.css'

export type PlanProviderMenuAction =
  | 'assign-missing'
  | 'replace-all'
  | 'clear-all'

const DEFAULT_PROVIDER_MENU_DISABLED: TxPlanProviderMenuAvailability = {
  assignMissingDisabled: true,
  replaceAllDisabled: true,
  clearAllDisabled: true,
}

const formatBillingDiscountPercent = (value: number): string => {
  const n = Number(value)
  if (Number.isNaN(n)) {
    return '0'
  }
  return String(parseFloat(n.toFixed(10)))
}

const selectedCheck = (
  <span className="plan-action-menu-check" aria-hidden>
    ✓
  </span>
)

interface PlanActionMenuButtonProps {
  tabKey: string
  isLocalDraft: boolean
  insuranceList: InsurancePlan[]
  selectedInsurance: InsurancePlan | null
  billingDiscounts: Discount[]
  selectedDiscountId: string
  customDiscount: number
  treatmentPlans: ITreatmentPlan[]
  onMenuSelect: (value: string, tabKey: string) => void
  onInsuranceSelect: (carrier: string, tabKey: string) => void
  onDiscountSelect: (discountId: string, tabKey: string) => void
  onCustomDiscountChange: (value: number, tabKey: string) => void
  onDuplicatePlan?: (sourcePlanId: string) => void
  providerMenuDisabled?: TxPlanProviderMenuAvailability
  onProviderMenuSelect?: (
    action: PlanProviderMenuAction,
    tabKey: string,
  ) => void
}

const PlanActionMenuButton: React.FC<PlanActionMenuButtonProps> = ({
  tabKey,
  isLocalDraft,
  insuranceList,
  selectedInsurance,
  billingDiscounts,
  selectedDiscountId,
  customDiscount,
  treatmentPlans,
  onMenuSelect,
  onInsuranceSelect,
  onDiscountSelect,
  onCustomDiscountChange,
  onDuplicatePlan,
  providerMenuDisabled = DEFAULT_PROVIDER_MENU_DISABLED,
  onProviderMenuSelect,
}): React.ReactElement => {
  const [menuOpen, setMenuOpen] = useState(false)
  const [showCustomDiscountInput, setShowCustomDiscountInput] = useState(false)
  const [customDiscountValue, setCustomDiscountValue] = useState('')
  const customInputRef = useRef<HTMLInputElement>(null)
  const skipCustomCommitOnCloseRef = useRef(false)

  const uniqueCarriers = useMemo((): string[] => {
    const carriers = insuranceList
      .map((plan) => plan.carrier_name)
      .filter((carrier): carrier is string => Boolean(carrier))
    return Array.from(new Set(carriers))
  }, [insuranceList])

  useEffect(() => {
    if (selectedDiscountId !== TX_PLAN_DISCOUNT_CUSTOM_ID) {
      setShowCustomDiscountInput(false)
    }
  }, [selectedDiscountId])

  const commitCustomDiscount = useCallback((): void => {
    if (customDiscountValue !== '' && Number(customDiscountValue) > 0) {
      onDiscountSelect(TX_PLAN_DISCOUNT_CUSTOM_ID, tabKey)
    }
    setShowCustomDiscountInput(false)
    setMenuOpen(false)
  }, [customDiscountValue, onDiscountSelect, tabKey])

  const selectReplacementDiscount = useCallback(
    (discountId: string): void => {
      skipCustomCommitOnCloseRef.current = true
      onDiscountSelect(discountId, tabKey)
      setShowCustomDiscountInput(false)
    },
    [onDiscountSelect, tabKey],
  )

  const handleMenuOpenChange = useCallback(
    (nextOpen: boolean): void => {
      if (!nextOpen && showCustomDiscountInput) {
        if (skipCustomCommitOnCloseRef.current) {
          skipCustomCommitOnCloseRef.current = false
          setShowCustomDiscountInput(false)
          setMenuOpen(false)
          return
        }
        commitCustomDiscount()
        return
      }
      setMenuOpen(nextOpen)
    },
    [commitCustomDiscount, showCustomDiscountInput],
  )

  const items = useMemo((): ContextualMenuItem[] => {
    const next: ContextualMenuItem[] = []
    if (treatmentPlans.length > 0 && onDuplicatePlan) {
      next.push({
        id: 'select-duplicate',
        label: 'Duplicate plan',
        items: treatmentPlans.map((plan) => ({
          id: `duplicate-plan-${plan.tp_id}`,
          label: plan.tp_heading || 'Untitled Plan',
          onSelect: (): void => {
            onDuplicatePlan(plan.tp_id)
          },
        })),
      })
    }
    if (uniqueCarriers.length > 0) {
      next.push({
        id: 'select-insurance',
        label: 'Insurance',
        items: uniqueCarriers.map((carrier) => ({
          id: `insurance-${carrier}`,
          label: carrier,
          icon:
            selectedInsurance?.carrier_name === carrier
              ? selectedCheck
              : undefined,
          onSelect: (): void => {
            onInsuranceSelect(carrier, tabKey)
          },
        })),
      })
    }
    const isCustomSelected = selectedDiscountId === TX_PLAN_DISCOUNT_CUSTOM_ID
    const customDiscountText =
      isCustomSelected && customDiscount > 0
        ? `Custom ${customDiscount}%`
        : 'Custom'
    next.push({
      id: 'select-discount',
      label: 'Discount',
      items: [
        {
          id: 'discount-none',
          label: 'No Discount',
          icon: selectedDiscountId === '' ? selectedCheck : undefined,
          onSelect: (): void => {
            selectReplacementDiscount('')
          },
        },
        ...billingDiscounts.map((disc) => ({
          id: `discount-preset:${disc.discount_id}`,
          label: `${disc.description} ${formatBillingDiscountPercent(disc.discount_percentage)}%`,
          icon:
            selectedDiscountId === disc.discount_id ? selectedCheck : undefined,
          onSelect: (): void => {
            selectReplacementDiscount(disc.discount_id)
          },
        })),
        {
          id: 'discount-custom',
          label: customDiscountText,
          icon: isCustomSelected ? selectedCheck : undefined,
          keepOpenOnSelect: true,
          onSelect: (): void => {
            setCustomDiscountValue(
              selectedDiscountId === TX_PLAN_DISCOUNT_CUSTOM_ID
                ? customDiscount.toString()
                : '0',
            )
            setShowCustomDiscountInput(true)
            setMenuOpen(true)
          },
          renderAfter: showCustomDiscountInput ? (
            <div className="custom-discount-input-wrapper">
              <input
                ref={customInputRef}
                className="custom-discount-input"
                aria-label="Custom discount percentage"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={customDiscountValue}
                onChange={(event): void => {
                  const value = event.target.value
                  setCustomDiscountValue(value)
                  const numValue = value === '' ? 0 : Number(value)
                  onCustomDiscountChange(numValue, tabKey)
                }}
                onBlur={commitCustomDiscount}
                onKeyDown={(event): void => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    commitCustomDiscount()
                  }
                  if (event.key === 'Escape') {
                    setShowCustomDiscountInput(false)
                    setCustomDiscountValue(customDiscount.toString())
                    setMenuOpen(false)
                  }
                }}
              />
            </div>
          ) : undefined,
        },
      ],
    })
    if (onProviderMenuSelect) {
      next.push({
        id: 'select-providers',
        label: 'Providers',
        items: [
          {
            id: 'provider-menu-assign',
            label: 'Assign missing providers',
            disabled: providerMenuDisabled.assignMissingDisabled,
            onSelect: (): void => {
              onProviderMenuSelect('assign-missing', tabKey)
            },
          },
          {
            id: 'provider-menu-replace',
            label: 'Replace all providers',
            disabled: providerMenuDisabled.replaceAllDisabled,
            onSelect: (): void => {
              onProviderMenuSelect('replace-all', tabKey)
            },
          },
          {
            id: 'provider-menu-clear',
            label: 'Clear all providers',
            disabled: providerMenuDisabled.clearAllDisabled,
            onSelect: (): void => {
              onProviderMenuSelect('clear-all', tabKey)
            },
          },
        ],
      })
    }
    next.push({
      id: 'delete-plan',
      label: isLocalDraft ? 'Discard draft' : 'Delete plan',
      onSelect: (): void => {
        onMenuSelect('delete-plan', tabKey)
      },
    })
    return next
  }, [
    billingDiscounts,
    commitCustomDiscount,
    customDiscount,
    customDiscountValue,
    isLocalDraft,
    onCustomDiscountChange,
    onDiscountSelect,
    onDuplicatePlan,
    onInsuranceSelect,
    selectReplacementDiscount,
    onMenuSelect,
    onProviderMenuSelect,
    providerMenuDisabled,
    selectedDiscountId,
    selectedInsurance?.carrier_name,
    showCustomDiscountInput,
    tabKey,
    treatmentPlans,
    uniqueCarriers,
  ])

  useEffect(() => {
    if (!showCustomDiscountInput) {
      return
    }
    customInputRef.current?.focus()
  }, [showCustomDiscountInput])

  return (
    <div className="plan-action-menu-container">
      <ContextualMenu
        ariaLabel="Plan actions"
        items={items}
        open={menuOpen}
        onOpenChange={handleMenuOpenChange}
      />
    </div>
  )
}

export default PlanActionMenuButton
